import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styled, TooltipProps } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { useTranslate } from "../../hooks/useTranslate";
import { usePatientFeatureFlags } from "../../hooks/usePatientFeatureFlags";
import { PatientColorMap } from "../../layout/patientColorMap";
import { usePatientBackdrop } from "../../state/stateManagers/useBackdrop";
import { useWidgetSettings } from "../../widget/useWidgetSettings";

//region [[ Styles ]]
const ContentContainer = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "white",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    marginTop: 0,
    backgroundColor: "white",
    padding: "10px",
    color: PatientColorMap.Default,
    fontSize: "14px",
  },
}));

const TooltipItem = styled("span")(({ theme }) => ({
  position: "relative",
  zIndex: theme.zIndex.drawer + 2,
  background: "white",
  borderRadius: "50%",
  ".tooltip-highlight": {
    zIndex: theme.zIndex.drawer + 2,
  },
}));
//endregion [[ Styles ]]

//region [[ Props ]]

export interface FirstTimeShowTooltipProps {
  children: React.ReactNode;
  tooltipKey?: string;
  orientation?: "TOP" | "LEFT";
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const FirstTimeShowTooltip = ({
  ...props
}: FirstTimeShowTooltipProps) => {
  const { translate } = useTranslate();
  const widgetSettings = useWidgetSettings();
  const [showTooltip, setShowTooltip] = useState(false);

  const handleCloseBackdrop = useCallback(() => {
    setShowTooltip(false);
  }, []);

  const tooltipKey = useMemo(
    () => `x-tooltip-${props.tooltipKey.trim().substring(0, 15)}`,
    [props.tooltipKey],
  );

  const { showBackdrop } = usePatientBackdrop(tooltipKey, handleCloseBackdrop);

  const tooltipFeatureFlagOn =
    usePatientFeatureFlags("showFirstTimeButtonsTooltip") &&
    (!widgetSettings as boolean); // Don't show backdrop if that's a widget

  useEffect(() => {
    if (!tooltipFeatureFlagOn) return;

    if (sessionStorage.getItem(tooltipKey)) {
      setShowTooltip(false);
      showBackdrop(false, tooltipKey);
    } else {
      // Give it a second to render the button before showing the tooltip
      setTimeout(() => {
        setShowTooltip(true);
        showBackdrop(true, tooltipKey);
      }, 750);
      sessionStorage.setItem(tooltipKey, "true");
    }
  }, [tooltipKey, showBackdrop, tooltipFeatureFlagOn]);

  if (!tooltipFeatureFlagOn || !showTooltip) return <>{props.children}</>;

  return (
    <ContentContainer
      className={"tooltip-content-container"}
      open={true}
      arrow
      title={translate(props.tooltipKey)}
    >
      <TooltipItem className={"tooltip-item"}>{props.children}</TooltipItem>
    </ContentContainer>
  );
};
